import React from 'react';
import { ShieldAlert, ShieldX, Settings, Lock } from 'lucide-react';
import { PermissionStatus, PERMISSION_MESSAGES } from '../../../hooks/usePermissionCheck';

interface PermissionDeniedProps {
  /** Status da permissão */
  status: PermissionStatus;
  /** Mensagem customizada (sobrescreve a padrão) */
  message?: string;
  /** Título customizado */
  title?: string;
  /** Callback para botão de voltar */
  onBack?: () => void;
  /** Texto do botão de voltar */
  backLabel?: string;
  /** Variante visual */
  variant?: 'full' | 'inline' | 'compact';
  /** Classe CSS adicional */
  className?: string;
}

/**
 * Componente para exibir mensagem de permissão negada
 * 
 * @example
 * // Uso simples
 * <PermissionDenied status="denied" onBack={() => navigate('/')} />
 * 
 * // Com mensagem customizada
 * <PermissionDenied 
 *   status="not_configured" 
 *   message="Esta ferramenta ainda não está disponível para você."
 *   onBack={handleBack}
 * />
 */
export function PermissionDenied({
  status,
  message,
  title,
  onBack,
  backLabel = 'Voltar',
  variant = 'full',
  className = ''
}: PermissionDeniedProps) {
  // Ícone baseado no status
  const Icon = {
    denied: ShieldX,
    not_configured: Settings,
    admin_only: Lock,
    allowed: ShieldAlert // Não deveria acontecer, mas fallback
  }[status] || ShieldAlert;

  // Cor baseada no status
  const iconColor = {
    denied: 'text-red-400',
    not_configured: 'text-yellow-400',
    admin_only: 'text-purple-400',
    allowed: 'text-green-400'
  }[status] || 'text-red-400';

  // Título baseado no status
  const defaultTitle = {
    denied: 'Acesso Negado',
    not_configured: 'Recurso Não Configurado',
    admin_only: 'Acesso Restrito',
    allowed: 'Permitido'
  }[status] || 'Acesso Negado';

  const displayMessage = message || PERMISSION_MESSAGES[status];
  const displayTitle = title || defaultTitle;

  // Variante compacta (apenas texto inline)
  if (variant === 'compact') {
    return (
      <div className={`flex items-center gap-2 text-sm ${iconColor} ${className}`}>
        <Icon className="w-4 h-4" />
        <span>{displayMessage}</span>
      </div>
    );
  }

  // Variante inline (card pequeno)
  if (variant === 'inline') {
    return (
      <div className={`flex items-center gap-3 p-3 bg-navy-800/50 border border-navy-700 rounded-lg ${className}`}>
        <Icon className={`w-5 h-5 ${iconColor}`} />
        <div className="flex-1">
          <p className="text-sm font-medium text-white">{displayTitle}</p>
          <p className="text-xs text-gray-400">{displayMessage}</p>
        </div>
        {onBack && (
          <button
            onClick={onBack}
            className="px-3 py-1 text-xs bg-navy-700 text-white rounded hover:bg-navy-600 transition-colors"
          >
            {backLabel}
          </button>
        )}
      </div>
    );
  }

  // Variante full (tela cheia / modal)
  return (
    <div className={`fixed inset-0 bg-navy-900/80 backdrop-blur-sm flex items-center justify-center z-50 ${className}`}>
      <div className="bg-navy-800 rounded-lg w-full max-w-md p-6 text-center animate-fadeIn">
        <div className={`w-16 h-16 mx-auto mb-4 rounded-full bg-navy-700/50 flex items-center justify-center`}>
          <Icon className={`w-8 h-8 ${iconColor}`} />
        </div>
        
        <h2 className="text-xl font-bold text-white mb-2">{displayTitle}</h2>
        <p className="text-gray-400 mb-6">{displayMessage}</p>
        
        {status === 'not_configured' && (
          <p className="text-xs text-gray-500 mb-4">
            Código de referência: <code className="bg-navy-700 px-1 rounded">PERM_NOT_CFG</code>
          </p>
        )}
        
        {onBack && (
          <button
            onClick={onBack}
            className="px-6 py-2 bg-navy-700 text-white rounded-lg hover:bg-navy-600 transition-colors"
          >
            {backLabel}
          </button>
        )}
      </div>
    </div>
  );
}

export default PermissionDenied;

